import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { I18nProvider } from "@/lib/i18n";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { AccessibilityPanelProvider } from "@/components/AccessibilityWidget";
import { ArticleReaderProvider } from "@/components/ArticleReader";
import { Toaster } from "@/components/ui/sonner";
import { SITE_URL } from "@/lib/site";
import { pic } from "@/lib/responsive-images";

import appCss from "../styles.css?url";

// Non-critical, interactive-only chrome — code-split so it stays out of the
// initial bundle and hydrates after the above-the-fold content.
const StickyCTA = lazy(() =>
  import("@/components/StickyCTA").then((m) => ({ default: m.StickyCTA })),
);
const AccessibilityWidget = lazy(() =>
  import("@/components/AccessibilityWidget").then((m) => ({
    default: m.AccessibilityWidget,
  })),
);

// Header logo (above the fold on every route) — used for the LCP preload.
const logo = pic("logo-horizontal");

// Google Fonts stylesheet, loaded non-render-blocking (see RootShell).
const GOOGLE_FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Playfair+Display:wght@500;600;700&display=swap";

// Strict Content-Security-Policy delivered as an in-<head> meta tag for
// defense-in-depth (the primary policy is the SSR response header in
// src/start.ts). Emitted in production only — dev omits it so Vite HMR
// (which needs 'unsafe-eval' + ws:) keeps working. `frame-ancestors`,
// `report-uri` and `sandbox` are intentionally left to the header, since a
// <meta> CSP ignores them per spec.
function buildCspMeta(): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? "";
  let supabaseHttp = "";
  let supabaseWs = "";
  try {
    const u = new URL(supabaseUrl);
    supabaseHttp = u.origin;
    supabaseWs = `wss://${u.host}`;
  } catch {
    /* no supabase configured */
  }
  const connect = [
    "'self'",
    supabaseHttp,
    supabaseWs,
    "https://dgr2n1ui.api.sanity.io",
    "wss://dgr2n1ui.api.sanity.io",
    "https://*.api.sanity.io",
    "wss://*.api.sanity.io",
    "https://*.apicdn.sanity.io",
    "https://www.google-analytics.com",
    "https://region1.google-analytics.com",
    "https://www.googletagmanager.com",
    "https://stats.g.doubleclick.net",
  ].filter(Boolean);
  return [
    "default-src 'self'",
    "base-uri 'self'",
    "object-src 'none'",
    "form-action 'self'",
    "img-src 'self' data: blob: https:",
    "font-src 'self' https://fonts.gstatic.com data:",
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
    "script-src 'self' 'unsafe-inline' https://www.googletagmanager.com https://www.google-analytics.com",
    `connect-src ${connect.join(" ")}`,
    "frame-src 'self' https://leadflow.puulse.ai https://www.google.com",
    "worker-src 'self' blob:",
    "manifest-src 'self'",
    "upgrade-insecure-requests",
  ].join("; ");
}



const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "RoofingContractor",
  name: "AMR 3 Design & Construction LLC",
  "@id": `${SITE_URL}/#business`,
  url: SITE_URL,
  logo: `${SITE_URL}/android-chrome-512x512.png`,
  image: `${SITE_URL}/og-image-v3.jpg`,
  telephone: "+1-505-868-0847",
  hasMap: "https://www.google.com/maps?cid=6621967010212129613",
  sameAs: ["https://www.google.com/maps?cid=6621967010212129613"],
  address: {
    "@type": "PostalAddress",
    streetAddress: "Albuquerque",
    addressLocality: "Albuquerque",
    addressRegion: "NM",
    postalCode: "87113",
    addressCountry: "US",
  },
  areaServed: "New Mexico",
  priceRange: "$$",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "07:00",
      closes: "19:00",
    },
  ],
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "AMR 3 Design & Construction",
  url: SITE_URL,
  inLanguage: ["en-US", "es-MX"],
  publisher: {
    "@type": "Organization",
    name: "AMR 3 Design & Construction LLC",
    url: SITE_URL,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/og-image-v3.jpg` },
  },
};

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-7xl font-bold text-primary">404</h1>
        <p className="mt-4 text-muted-foreground">This page doesn't exist.</p>
        <Link
          to="/"
          className="mt-6 inline-flex rounded-sm bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground"
        >
          Go home
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-2xl text-primary">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="mt-6 rounded-sm bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      ...(import.meta.env.PROD
        ? [{ httpEquiv: "Content-Security-Policy", content: buildCspMeta() }]
        : []),
      { title: "AMR 3 Design & Construction — Roofing & Solar Experts in Albuquerque NM" },
      {
        name: "description",
        content: "Licensed roofing and solar contractor serving Albuquerque and all of New Mexico. Expert consultations, expert installation, lasting protection for your home.",
      },
      { name: "author", content: "AMR 3 Design & Construction LLC" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "AMR 3 Design & Construction" },
      { property: "og:title", content: "AMR 3 Design & Construction — Roofing & Solar Experts in Albuquerque NM" },
      {
        property: "og:description",
        content: "Licensed roofing and solar contractor serving Albuquerque and all of New Mexico. Expert consultations, expert installation, lasting protection for your home.",
      },
      { property: "og:image", content: `${SITE_URL}/og-image-v3.jpg` },
      { property: "og:image:secure_url", content: `${SITE_URL}/og-image-v3.jpg` },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "AMR 3 Design & Construction — Roofing & Solar Experts, Albuquerque NM, Request Consultation" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@AMR3Construction" },
      { name: "twitter:title", content: "AMR 3 Design & Construction — Roofing & Solar Experts in Albuquerque NM" },
      { name: "twitter:description", content: "Licensed roofing and solar contractor serving Albuquerque and all of New Mexico. Expert consultations, expert installation, lasting protection for your home." },
      { name: "twitter:image", content: `${SITE_URL}/og-image-v3.jpg` },
      { name: "theme-color", content: "#3b2a1a" },
      { name: "application-name", content: "AMR 3 Design & Construction" },
      { name: "apple-mobile-web-app-title", content: "AMR 3" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "msapplication-TileColor", content: "#3b2a1a" },
      { name: "msapplication-TileImage", content: "/mstile-150x150.png" },
      { name: "msapplication-config", content: "/browserconfig.xml" },
      { name: "format-detection", content: "telephone=no" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "icon", type: "image/png", href: "/favicon-16x16.png", sizes: "16x16" },
      { rel: "icon", type: "image/png", href: "/favicon-32x32.png", sizes: "32x32" },
      { rel: "icon", type: "image/png", href: "/favicon-96x96.png", sizes: "96x96" },
      { rel: "icon", type: "image/png", href: "/android-chrome-192x192.png", sizes: "192x192" },
      { rel: "icon", type: "image/png", href: "/android-chrome-512x512.png", sizes: "512x512" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "apple-touch-icon", sizes: "152x152", href: "/apple-touch-icon-152x152.png" },
      { rel: "apple-touch-icon", sizes: "167x167", href: "/apple-touch-icon-167x167.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon-180x180.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      // Discover the above-the-fold logo (LCP candidate) as early as possible.
      {
        rel: "preload",
        as: "image",
        href: logo.src,
        imageSrcSet: logo.srcSet,
        imageSizes: "140px",
        fetchPriority: "high",
      },
      // Fonts: warm up the connection and pre-fetch the CSS as a resource so it
      // does not block first render. The actual <link rel="stylesheet"> is
      // applied non-render-blocking in RootShell.
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preload", as: "style", href: GOOGLE_FONTS_HREF },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(websiteJsonLd) },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        {/* Trusted Types: register a permissive `default` policy BEFORE any
            other script so `require-trusted-types-for 'script'` (see CSP in
            src/start.ts) funnels every innerHTML / script-url sink through one
            auditable choke point instead of blocking the app. This is the
            standard interim step for a Trusted Types rollout; tighten the
            policy (e.g. DOMPurify) later to fully enforce sanitisation. */}
        <script
          dangerouslySetInnerHTML={{
            __html:
              "(function(){if(window.trustedTypes&&window.trustedTypes.createPolicy){try{window.trustedTypes.createPolicy('default',{createHTML:function(s){return s},createScript:function(s){return s},createScriptURL:function(s){return s}})}catch(e){}}})();",
          }}
        />
        <HeadContent />


        {/* Non-render-blocking Google Fonts: the browser downloads the CSS with
            media="print" (so it never blocks first paint), then the inline
            script flips it to media="all" to apply it. font-display: swap keeps
            text visible in a fallback until the webfonts arrive. */}
        <link
          id="gf-css"
          rel="stylesheet"
          href={GOOGLE_FONTS_HREF}
          media="print"
        />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "(function(){var l=document.getElementById('gf-css');if(l){l.media='all';}})();",
          }}
        />
        <noscript>
          <link rel="stylesheet" href={GOOGLE_FONTS_HREF} />
        </noscript>

        {/* Google Analytics 4 */}
        <script
          dangerouslySetInnerHTML={{
            __html:
              "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-TZPL3W24D1');(function(){var d=false;function l(){if(d)return;d=true;var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=G-TZPL3W24D1';document.head.appendChild(s);}function i(){(window.requestIdleCallback||function(f){setTimeout(f,1500)})(l,{timeout:3000});}if(document.readyState==='complete')i();else window.addEventListener('load',i);['scroll','pointerdown','keydown','touchstart'].forEach(function(e){window.addEventListener(e,l,{once:true,passive:true});});})();",
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "document.addEventListener('click',function(e){var a=e.target&&e.target.closest&&e.target.closest('a[href^=\"tel:\"]');if(!a)return;if(typeof window.gtag==='function'){window.gtag('event','call',{phone_number:a.getAttribute('href').replace('tel:','')});}},true);",
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html:
              "(function(){var r=function(){document.documentElement.setAttribute('data-app-ready','true')};if(document.readyState!=='loading'){requestAnimationFrame(r)}else{document.addEventListener('DOMContentLoaded',function(){requestAnimationFrame(r)})};setTimeout(r,800)})();",
          }}
        />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (typeof document !== "undefined") {
    queueMicrotask(() => document.documentElement.setAttribute("data-app-ready", "true"));
  }
  const isAdmin = pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <Toaster />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <AccessibilityPanelProvider>
          <ArticleReaderProvider>
            <div className="flex min-h-screen flex-col">
              <SiteHeader />
              <main className="flex-1">
                <span key={`sweep-${pathname}`} className="page-sweep-overlay" aria-hidden />
                <div key={pathname} className="animate-page-enter">
                  <Outlet />
                </div>
              </main>
              <SiteFooter />
            </div>
            <Suspense fallback={null}>
              <StickyCTA />
              <AccessibilityWidget />
            </Suspense>
            <Toaster />
          </ArticleReaderProvider>
        </AccessibilityPanelProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
